import React, { useState, useEffect } from 'react';
import { getPendingDisputes, approveDisputes, rejectDisputes } from '../services/disputeApprovalService';
import { Dispute, getImpactedTransactions } from '../services/disputeService';
import { getMasterTransactionById } from '../services/allocationService';
import { getStaff, Staff } from '../services/staffService';
import { useFirebase } from '../contexts/FirebaseContext';
import { Users, AlertTriangle, ChevronRight, ChevronDown, CheckCircle2, Calendar, ShieldAlert, Edit } from 'lucide-react';
import { DisputeEditModal } from './DisputeEditModal';
import { getIRSCashTipDeadline, formatLongDate } from '../lib/complianceUtils';
import { formatDisputeReason, getDisputeReasonInfo, isFinancialDispute, isSupportInquiry } from '../lib/disputeUtils';

interface Props {
  tenantId: string;
  onSwitchToInquiries?: () => void;
}

export const DisputeApproval: React.FC<Props> = ({ tenantId, onSwitchToInquiries }) => {
  const { user } = useFirebase();
  const [pendingDisputes, setPendingDisputes] = useState<(Dispute & { id: string })[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [pendingInquiriesCount, setPendingInquiriesCount] = useState(0);
  const [selectedDisputes, setSelectedDisputes] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingDispute, setEditingDispute] = useState<(Dispute & { id: string }) | null>(null);
  const [impactData, setImpactData] = useState<any[]>([]);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);

  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [managerComment, setManagerComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const checkCompliance = (dispute: Dispute) => {
    const isCash = dispute.tipType === 'Cash Tip' || dispute.originalTipType === 'Cash';
    if (!isCash || !dispute.tipDate) return null;
    
    const parts = dispute.tipDate.split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    
    const deadline = getIRSCashTipDeadline(year, month);
    const today = new Date();
    const todayReset = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isLate = todayReset > deadline;
    
    return {
      deadline,
      deadlineStr: formatLongDate(deadline),
      isLate,
      monthName: new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' }),
      year
    };
  };

  const fetchDisputes = async () => {
    try {
      const [data, staff] = await Promise.all([
        getPendingDisputes(tenantId),
        getStaff(tenantId)
      ]);
      setStaffList(staff);
      const financialDisputes = (data || []).filter(d => isFinancialDispute(d));
      const inquiryDisputes = (data || []).filter(d => isSupportInquiry(d));
      
      setPendingDisputes(financialDisputes);
      setPendingInquiriesCount(inquiryDisputes.length);
    } catch (err) {
      console.error('Error fetching disputes:', err);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, [tenantId]);

  const toggleDispute = (id: string) => {
    const newSelected = new Set(selectedDisputes);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedDisputes(newSelected);
    setError(null);
  };

  const analyzeImpact = async (dispute: Dispute & { id: string }) => {
    if (expandedId === dispute.id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(dispute.id);
    setIsLoadingImpact(true);
    setImpactData([]);

    try {
      if (dispute.masterTxId) {
        const tx = await getMasterTransactionById(tenantId, dispute.masterTxId);
        if (tx?.parentTxId) {
          const impacted = await getImpactedTransactions(tenantId, tx.parentTxId);
          setImpactData(impacted);
        }
      }
    } catch (err) {
      console.error('Error analyzing impact:', err);
    } finally {
      setIsLoadingImpact(false);
    }
  };

  const handleApprove = async () => {
    if (!user?.email) {
      setError('You must be signed in to approve disputes.');
      return;
    }
    
    setIsApproving(true);
    setError(null);
    setSuccess(null);

    try {
      const disputesToApprove = pendingDisputes.filter(d => selectedDisputes.has(d.id));
      await approveDisputes(tenantId, disputesToApprove, user.email, managerComment);
      await fetchDisputes();
      setSelectedDisputes(new Set());
      setManagerComment('');
      setSuccess(`Successfully approved ${disputesToApprove.length} financial disputes with your feedback.`);
    } catch (err) {
      console.error('Error approving disputes:', err);
      setError('Failed to approve disputes. This may be due to permission restrictions or invalid data.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!user?.email) {
      setError('You must be signed in to reject disputes.');
      return;
    }
    
    setIsRejecting(true);
    setError(null);
    setSuccess(null);

    try {
      const disputesToReject = pendingDisputes.filter(d => selectedDisputes.has(d.id));
      await rejectDisputes(tenantId, disputesToReject, user.email, managerComment);
      await fetchDisputes();
      setSelectedDisputes(new Set());
      setManagerComment('');
      setSuccess(`Successfully rejected ${disputesToReject.length} disputes.`);
    } catch (err) {
      console.error('Error rejecting disputes:', err);
      setError('Failed to reject disputes.');
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className="p-8 bg-white shadow-sm border border-gray-200 rounded-xl font-sans">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-black italic uppercase tracking-tighter">
            Financial Tip Disputes & Allocations
          </h2>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            Strictly monetary adjustments affecting tip totals, shift pools, and payroll ledger exports.
          </p>
        </div>
      </div>

      {pendingInquiriesCount > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 text-white rounded-xl">
              <Users size={16} />
            </div>
            <div>
              <p className="text-xs font-bold text-blue-900">
                You have {pendingInquiriesCount} pending Support / Benchmark {pendingInquiriesCount === 1 ? 'Inquiry' : 'Inquiries'}
              </p>
              <p className="text-[11px] text-blue-700 font-medium">
                General questions and schedule inquiries are managed separately in the Support Inquiries tab.
              </p>
            </div>
          </div>
          {onSwitchToInquiries && (
            <button
              onClick={onSwitchToInquiries}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shrink-0 shadow-sm"
            >
              View Inquiries ({pendingInquiriesCount})
            </button>
          )}
        </div>
      )}
      
      <div className="mb-8 p-6 bg-gray-50 border border-gray-100 rounded-2xl">
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Internal Management Note (Shared with Employee)</label>
        <textarea 
          value={managerComment}
          onChange={e => setManagerComment(e.target.value)}
          placeholder="Add a reason or guidance for this approval/correction..."
          className="input-field w-full min-h-[80px] bg-white"
        />
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[10px] text-gray-500 italic max-w-xs">Selecting multiple disputes will apply this same note to all of them.</p>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleReject}
              disabled={selectedDisputes.size === 0 || isRejecting}
              className={`px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 transition-all shadow-lg ${
                selectedDisputes.size === 0 || isRejecting 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                  : 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/20'
              }`}
            >
              {isRejecting ? 'Processing...' : `Reject (${selectedDisputes.size})`}
            </button>
            <button 
              onClick={handleApprove}
              disabled={selectedDisputes.size === 0 || isApproving}
              className={`px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 transition-all shadow-lg ${
                selectedDisputes.size === 0 || isApproving 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
              }`}
            >
              {isApproving ? 'Processing...' : `Approve Correction Set (${selectedDisputes.size})`}
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-in fade-in slide-in-from-top-1">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm animate-in fade-in slide-in-from-top-1">
            {success}
          </div>
        )}
      </div>

      {/* Mobile view */}
      <div className="md:hidden space-y-4">
        {pendingDisputes.map(d => {
          const matchedStaff = staffList.find(s => 
            (d.staffId && s.id === d.staffId) || 
            (s.email && d.email && s.email.toLowerCase() === d.email.toLowerCase()) ||
            (s.name && d.staffName && s.name.toLowerCase() === d.staffName.toLowerCase())
          );
          const resolvedPosId = d.posId || matchedStaff?.posId;
          const resolvedStaffId = d.staffId || matchedStaff?.id;
          const resolvedName = d.staffName || matchedStaff?.name || d.email;

          return (
            <div key={d.id} className={`p-4 rounded-xl border transition-all ${expandedId === d.id ? 'bg-amber-50/50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-3">
                  <input type="checkbox" checked={selectedDisputes.has(d.id)} onChange={() => toggleDispute(d.id)} className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <div className="space-y-1">
                    <p className="font-black text-gray-900">{resolvedName}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {resolvedPosId && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase">
                          POS ID: {resolvedPosId}
                        </span>
                      )}
                      {resolvedStaffId && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                          Staff ID: {resolvedStaffId.slice(0, 8)}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{d.tipDate}</p>
                    {(() => {
                      const comp = checkCompliance(d);
                      if (!comp) return null;
                      return comp.isLate ? (
                        <span className="text-[8px] font-black text-amber-600 uppercase tracking-widest mt-0.5 block">
                          ⚠️ Late (Past {comp.deadlineStr})
                        </span>
                      ) : (
                        <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mt-0.5 block">
                          📋 On-Time (By {comp.deadlineStr})
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setEditingDispute(d)}
                    title="Edit Dispute"
                    className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-blue-600 transition-all"
                  >
                    <Edit size={18} />
                  </button>
                  <button 
                    onClick={() => analyzeImpact(d)}
                    className={`p-2 rounded-lg transition-all ${expandedId === d.id ? 'bg-amber-100 text-amber-600' : 'text-gray-400'}`}
                  >
                    {expandedId === d.id ? <ChevronDown size={18}/> : <Users size={18}/>}
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100 mb-3">
                <div>
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Original</p>
                  <p className="text-sm font-bold text-gray-500">${d.originalAmount.toFixed(2)}</p>
                </div>
                <ChevronRight size={14} className="text-gray-300" />
                <div className="text-right flex flex-col items-end">
                  <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest font-mono">Correction</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <p className="text-sm font-black text-emerald-700 font-mono">${d.correctedAmount.toFixed(2)}</p>
                    <button 
                      onClick={() => setEditingDispute(d)}
                      title="Edit Amount"
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>

              {d.reason && (() => {
                const reasonInfo = getDisputeReasonInfo(d.reason);
                const empComment = d.employeeNotes || d.explanation || (d as any).description || (d as any).notes;
                const mgrComment = d.managerNotes || (d as any).managerComment;
                return (
                  <div className="p-3 bg-white/70 rounded-lg border border-gray-100 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                        {reasonInfo.label}
                      </span>
                    </div>
                    {reasonInfo.description && (
                      <p className="text-xs text-gray-500 font-medium">{reasonInfo.description}</p>
                    )}
                    {empComment && (
                      <div className="bg-amber-50/80 border border-amber-200/70 rounded p-2 text-xs text-amber-950 font-medium mt-1">
                        <span className="font-black text-[9px] text-amber-800 uppercase tracking-wider block">Employee Note:</span>
                        "{empComment}"
                      </div>
                    )}
                    {mgrComment && (
                      <div className="bg-indigo-50/80 border border-indigo-200/70 rounded p-2 text-xs text-indigo-950 font-medium mt-1">
                        <span className="font-black text-[9px] text-indigo-800 uppercase tracking-wider block">Manager Review Note:</span>
                        "{mgrComment}"
                      </div>
                    )}
                  </div>
                );
              })()}

              {expandedId === d.id && (
                <div className="mt-4 pt-4 border-t border-amber-200 animate-in slide-in-from-top-1 duration-200">
                  <h4 className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Users size={12} />
                    Impacted Pool Members
                  </h4>
                  <div className="space-y-2">
                    {isLoadingImpact ? (
                      <div className="text-[10px] italic text-gray-400">Loading analysis...</div>
                    ) : impactData.length > 1 ? (
                      impactData.map(tx => {
                        const mStaff = staffList.find(s => (tx.staffId && s.id === tx.staffId) || (s.name && tx.staffName && s.name.toLowerCase() === tx.staffName.toLowerCase()));
                        return (
                          <div key={tx.id} className="flex justify-between items-center p-2 bg-white rounded border border-gray-100 text-[10px]">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-gray-700">{tx.staffName}</span>
                              {(tx.posId || mStaff?.posId) && (
                                <span className="px-1 py-0.2 rounded bg-blue-100 text-blue-800 font-mono text-[8px]">
                                  POS: {tx.posId || mStaff?.posId}
                                </span>
                              )}
                            </div>
                            <span className="font-mono font-bold">${tx.amount.toFixed(2)}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-[10px] text-gray-400 italic">No linked allocations found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Select</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Impact</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Staff Member</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Tip Date</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Original</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Corrected</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Variance</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {pendingDisputes.map(d => {
              const matchedStaff = staffList.find(s => 
                (d.staffId && s.id === d.staffId) || 
                (s.email && d.email && s.email.toLowerCase() === d.email.toLowerCase()) ||
                (s.name && d.staffName && s.name.toLowerCase() === d.staffName.toLowerCase())
              );
              const resolvedPosId = d.posId || matchedStaff?.posId;
              const resolvedStaffId = d.staffId || matchedStaff?.id;
              const resolvedName = d.staffName || matchedStaff?.name || d.email;

              return (
                <React.Fragment key={d.id}>
                  <tr className={`hover:bg-gray-50 transition-colors ${expandedId === d.id ? 'bg-amber-50/30' : ''}`}>
                    <td className="p-4 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedDisputes.has(d.id)} 
                        onChange={() => toggleDispute(d.id)} 
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                      />
                    </td>
                    <td className="p-4 flex gap-2 justify-center">
                      <button 
                        onClick={() => analyzeImpact(d)}
                        title="Analyze Pooled Impact"
                        className={`p-2 rounded-lg transition-all ${
                          expandedId === d.id ? 'bg-amber-100 text-amber-700' : 'text-gray-400 hover:bg-gray-100 hover:text-blue-600'
                        }`}
                      >
                        {expandedId === d.id ? <ChevronDown size={18}/> : <Users size={18}/>}
                      </button>
                      <button 
                        onClick={() => setEditingDispute(d)}
                        title="Edit Dispute"
                        className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-blue-600 transition-all"
                      >
                        <Edit size={18} />
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900">{resolvedName}</span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {resolvedPosId && (
                            <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                              POS ID: {resolvedPosId}
                            </span>
                          )}
                          {resolvedStaffId && (
                            <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                              Staff ID: {resolvedStaffId.slice(0, 8)}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter mt-0.5">{d.email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-600">{d.tipDate}</span>
                        {(() => {
                          const comp = checkCompliance(d);
                          if (!comp) return null;
                          return comp.isLate ? (
                            <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest mt-1 flex items-center gap-1" title={`Past IRS deadline of ${comp.deadlineStr}`}>
                              ⚠️ Late (Past {comp.deadlineStr})
                            </span>
                          ) : (
                            <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mt-1 flex items-center gap-1" title={`In compliance with IRS deadline of ${comp.deadlineStr}`}>
                              📋 On-Time (By {comp.deadlineStr})
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="p-4 text-gray-500 font-mono text-sm">${d.originalAmount.toFixed(2)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <span className="font-black text-green-700 font-mono text-base">${d.correctedAmount.toFixed(2)}</span>
                        <button 
                          onClick={() => setEditingDispute(d)}
                          title="Click to edit approved dispute amount"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors shadow-2xs"
                        >
                          <Edit size={12} /> Edit
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs font-black px-2 py-1 rounded inline-flex items-center gap-1 ${
                        d.correctedAmount > d.originalAmount ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {d.correctedAmount > d.originalAmount ? '+' : ''}${(d.correctedAmount - d.originalAmount).toFixed(2)}
                        {d.correctedAmount > d.originalAmount ? <ChevronRight size={12} className="-rotate-45"/> : <ChevronRight size={12} className="rotate-45"/>}
                      </span>
                    </td>
                    <td className="p-4">
                      {(() => {
                        const reasonInfo = getDisputeReasonInfo(d.reason);
                        const empComment = d.employeeNotes || d.explanation || (d as any).description || (d as any).notes;
                        const mgrComment = d.managerNotes || (d as any).managerComment;
                        return (
                          <div className="flex flex-col gap-1.5 max-w-sm">
                            <span className="text-sm font-bold text-gray-900">{reasonInfo.label}</span>
                            {reasonInfo.description && (
                              <span className="text-xs text-gray-500 font-medium leading-relaxed">{reasonInfo.description}</span>
                            )}
                            {empComment && (
                              <div className="bg-amber-50/90 border border-amber-200/80 rounded-lg p-2 text-xs text-amber-950 font-medium mt-0.5">
                                <span className="font-black text-[9px] text-amber-800 uppercase tracking-wider block mb-0.5">Employee Note:</span>
                                "{empComment}"
                              </div>
                            )}
                            {mgrComment && (
                              <div className="bg-indigo-50/90 border border-indigo-200/80 rounded-lg p-2 text-xs text-indigo-950 font-medium mt-0.5">
                                <span className="font-black text-[9px] text-indigo-800 uppercase tracking-wider block mb-0.5">Manager Review Note:</span>
                                "{mgrComment}"
                              </div>
                            )}
                            {d.masterTxId && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                                <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Pooled Transaction Identified</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  {expandedId === d.id && (
                    <tr className="bg-amber-50/20">
                      <td colSpan={8} className="p-0">
                        <div className="p-6 border-l-4 border-amber-400 animate-in slide-in-from-top-1 duration-200">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                                <Users size={20} />
                              </div>
                              <div>
                                <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Pooled Impact Analysis</h4>
                                <p className="text-xs text-gray-500 font-medium">Reviewing other staff members who shared this tip pool.</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block">Transaction Reference</span>
                              <span className="text-xs font-mono text-gray-600">TX-{d.masterTxId?.slice(-8)}</span>
                            </div>
                          </div>

                          {isLoadingImpact ? (
                            <div className="flex items-center gap-3 text-gray-400 italic text-sm p-4">
                              <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              Scanning master ledger for related allocations...
                            </div>
                          ) : impactData.length > 1 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {impactData.map((tx) => {
                                const mStaff = staffList.find(s => (tx.staffId && s.id === tx.staffId) || (s.name && tx.staffName && s.name.toLowerCase() === tx.staffName.toLowerCase()));
                                return (
                                  <div 
                                    key={tx.id} 
                                    className={`p-4 rounded-xl border transition-all ${
                                      tx.email === d.email 
                                        ? 'bg-white border-amber-300 shadow-sm ring-1 ring-amber-100' 
                                        : 'bg-white/50 border-gray-100 hover:border-gray-200'
                                    }`}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <p className="text-xs font-bold text-gray-900">{tx.staffName}</p>
                                        <div className="flex items-center gap-1 mt-0.5">
                                          {(tx.posId || mStaff?.posId) && (
                                            <span className="px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 font-mono text-[9px] font-bold">
                                              POS: {tx.posId || mStaff?.posId}
                                            </span>
                                          )}
                                          <span className="text-[10px] text-gray-400 font-mono">#{tx.id.slice(-8)}</span>
                                        </div>
                                      </div>
                                      {tx.email === d.email && (
                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[8px] font-black rounded uppercase tracking-widest">Disputing Party</span>
                                      )}
                                    </div>
                                    <div className="flex justify-between items-end">
                                      <div className="space-y-1">
                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Current Allocation</p>
                                        <p className="text-sm font-black text-gray-900">${tx.amount.toFixed(2)}</p>
                                      </div>
                                      <div className="text-right">
                                        {tx.email === d.email ? (
                                          <div className="flex flex-col items-end">
                                            <p className="text-[9px] font-bold text-blue-600 uppercase tracking-widest">Requested</p>
                                            <p className="text-sm font-black text-blue-600">${d.correctedAmount.toFixed(2)}</p>
                                          </div>
                                        ) : (
                                          <span className="text-[9px] text-gray-400 font-medium italic underline decoration-dotted">Static for this review</span>
                                        )}
                                      </div>
                                    </div>
                                    {tx.payrollIncluded && (
                                      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2">
                                        <CheckCircle2 size={12} className="text-blue-500" />
                                        <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest">Locked: Included in Payroll Export</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-500 text-sm">
                              <AlertTriangle size={18} className="text-gray-400" />
                              No other staff were linked to this specific transaction pool. This appears to be an isolated allocation.
                            </div>
                          )}
                          
                          <div className="mt-6 flex items-start gap-4 p-4 bg-white rounded-xl border border-amber-100">
                            <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                              <AlertTriangle size={20} />
                            </div>
                            <div>
                              <h5 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-1">Impact Summary</h5>
                              <p className="text-xs text-gray-600 leading-relaxed">
                                This dispute targets a **Pooled Allocation**. Approving this correction will create a specific **Adjustment Ledger Entry** for {resolvedName}. 
                                {d.type === 'Post-Payment' ? " Since this is a Post-Payment correction, it will be added as a catch-up payment to the next payroll, without deducting from others automatically." : " Verify if this correction implies that other staff allocations were also incorrect."}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {pendingDisputes.length === 0 && <p className="text-center text-gray-500 py-6">No pending disputes.</p>}
      {editingDispute && (
        <DisputeEditModal
          isOpen={!!editingDispute}
          onClose={() => setEditingDispute(null)}
          dispute={editingDispute}
          tenantId={tenantId}
          onSave={async () => {
             await fetchDisputes();
             setEditingDispute(null);
          }}
        />
      )}
    </div>
  );
};
